import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { ClientOptionService } from '@cutting/main/shared/services/client-options.service';
import { LanguageService } from '@cutting/main/shared/services/language.service';
import { UserSettingsService } from '@cutting/main/shared/services/user-settings.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseConfigService } from '@fuse/services/config.service';
import { CuttingClientIdentification, LocalizationHelper, NgxLocalizedNumbersService } from 'cloud-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '../../../../../../src/environments/environment';
import { LoginService } from './login.service';

@Component({
  selector: 'fuse-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: fuseAnimations,
})
export class FuseLoginComponent implements OnInit, OnDestroy {
  loginForm: UntypedFormGroup;
  loginFormErrors: any;
  hidePass = true;
  languages: any[] = [];
  sendingData = false;
  unsubscribe$: Subject<void> = new Subject();

  get userId(): number {
    return LocalStorageManager.regData;
  }
  set userId(val: number) {
    LocalStorageManager.regData = val;
  }

  constructor(
    private fuseConfigService: FuseConfigService,
    private formBuilder: UntypedFormBuilder,
    private loginService: LoginService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    private languageService: LanguageService,
    private localizedNumbersService: NgxLocalizedNumbersService,
    private settingsService: UserSettingsService,
    private clientOptionsService: ClientOptionService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.languages = LocalizationHelper.getLanguageList;

    // this.selectedLanguage = this.languages.find(l => l.id === this.translate.currentLang);
    // if (!this.selectedLanguage) {
    //   this.selectedLanguage = this.languages[0];
    // }

    this.fuseConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        toolbar: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        sidepanel: {
          hidden: true,
        },
      },
    };

    this.loginFormErrors = {
      login: {},
      password: {},
    };

    this.settingsService.clearCuttingSettings();
    this.settingsService.clearPresetSettings();
    this.clientOptionsService.clearClientOptions();
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      login: ['', [Validators.required]],
      password: ['', Validators.required],
    });

    if (
      (this.activatedRoute.snapshot.queryParams.email || this.activatedRoute.snapshot.queryParams.phone) &&
      this.activatedRoute.snapshot.queryParams.password
    ) {
      this.loginForm.controls.login.setValue(
        this.activatedRoute.snapshot.queryParams.email || this.activatedRoute.snapshot.queryParams.phone
      );
      this.loginForm.controls.password.setValue(this.activatedRoute.snapshot.queryParams.password);
      this.loginForm.updateValueAndValidity();

      const isMd5Password = !!this.boolMatch(this.activatedRoute.snapshot.queryParams.md5Password || '', [
        /true/i,
        /1/i,
      ]);
      this.onSubmit(isMd5Password);
    }

    this.loginForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.onLoginFormValuesChanged();
    });
  }

  onLoginFormValuesChanged(): void {
    for (const field in this.loginFormErrors) {
      // Clear previous errors
      this.loginFormErrors[field] = {};

      // Get the control
      const control = this.loginForm.get(field);

      if (control && control.dirty && !control.valid) {
        this.loginFormErrors[field] = control.errors;
      }
    }
  }

  /**
   * Set the language
   * @param lang
   */
  changeLanguage(lang: string): void {
    if (this.isDevMode()) {
      location.reload();
    } else {
      LocalStorageManager.currentLang = lang;
      window.location.href = window.location.origin + '/cutting/' + lang + '/#' + this.getCurrentRoute();
    }
  }

  getCurrentRoute(): string {
    return this.router.url;
  }

  isDevMode(): boolean {
    return !environment.production;
  }

  /*****************************************************************************************/

  onSubmit(md5Pass = false): void {
    if (this.loginForm.valid) {
      this.sendingData = true;

      this.loginClient(this.loginForm.controls.login.value, this.loginForm.controls.password.value, md5Pass);
    } else {
      this.snackBar.open($localize`Введите корректные данные`);
    }
  }

  loginClient(login: string, password: string, md5Pass = false): void {
    if (login !== '' && password !== '' && this.userId !== 0) {
      this.loginService.login(login, password, this.userId, this.locale, md5Pass).subscribe(
        (cl: CuttingClientIdentification) => {
          this.sendingData = false;
          LocalStorageManager.client = cl;

          const localizedCode = LocalizationHelper.getLocalizedNumberCodeByISOCode(cl.currencyCode);
          this.localizedNumbersService.setLocale(localizedCode);

          if (cl.languageCode !== this.locale) {
            window.location.href = window.location.origin + '/cutting/' + cl.languageCode + '/';
          } else {
            this.router.navigateByUrl('/order/list');
          }
        },
        (error: HttpErrorResponse) => {
          this.sendingData = false;

          if (+error.status === 403) {
            this.router.navigate(['/client/auth/lock']);
          } else if (+error.status === 406) {
            this.snackBar.open($localize`Некорректный email или пароль`);
          } else {
            this.snackBar.open($localize`Ошибка авторизации`);
          }
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private boolMatch(s: string, matchers: RegExp[]): boolean {
    let i;
    let matcher;
    const down = s.toLowerCase();
    matchers = [].concat(matchers);
    for (i = 0; i < matchers.length; i += 1) {
      matcher = matchers[i];
      if (!matcher) {
        continue;
      }
      if (matcher.test && matcher.test(s)) {
        return true;
      }
      if (matcher.toString().toLowerCase() === down) {
        return true;
      }
    }
  }
}
